/**
 * MongoDB 用户注册页面
 * 零侵入：完全独立于现有 Register.js
 * 路由：/register-v2
 */

import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Link,
  Grid,
  Checkbox,
  FormControlLabel,
  Divider,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Person as PersonIcon,
  Stars as StarsIcon,
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

const MongoRegister = () => {
  // 表单状态
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreement, setAgreement] = useState(false);
  
  // UI 状态
  const [success, setSuccess] = useState('');
  
  // 认证 Store
  const { 
    mongoRegister, 
    loading, 
    error, 
    mongoAvailable,
    switchAuthSystem 
  } = useAuthStore();
  
  const history = useHistory();

  // 表单验证
  const validateForm = () => {
    if (!username.trim()) {
      return '用户名不能为空';
    }
    if (username.length < 3) {
      return '用户名至少需要 3 个字符';
    }
    if (!email.trim()) {
      return '邮箱不能为空';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return '邮箱格式不正确';
    }
    if (!password) {
      return '密码不能为空';
    }
    if (password.length < 6) {
      return '密码长度至少需要 6 位';
    }
    if (password !== confirmPassword) {
      return '两次输入的密码不一致';
    }
    if (!agreement) {
      return '请同意用户协议和隐私政策';
    }
    return null;
  };

  // 提交注册
  const handleRegister = async (event) => {
    event.preventDefault();
    
    // 表单验证
    const validationError = validateForm();
    if (validationError) {
      // 错误会由 authStore 处理，这里可以不用额外设置
      return;
    }
    
    setSuccess('');
    
    const result = await mongoRegister(username, email, password);
    
    if (result.success) {
      setSuccess(`${result.message} 即将跳转到登录页面...`);
      setTimeout(() => {
        history.push('/login-v2');
      }, 2000);
    }
  };

  // 切换到旧系统
  const handleSwitchToLegacy = () => {
    switchAuthSystem('json');
    history.push('/register');
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper sx={{ mt: 8, p: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* MongoDB 徽章 */}
          <Chip
            icon={<StarsIcon />}
            label="MongoDB 新系统"
            color="primary"
            variant="outlined"
            sx={{ mb: 2 }}
          />
          
          <AccountCircleIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography component="h1" variant="h5">
            用户注册
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            新用户系统 · 支持会员等级和积分成长
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleRegister} sx={{ mt: 2 }}>
          {/* MongoDB 连接状态警告 */}
          {!mongoAvailable && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon fontSize="small" />
                <Typography variant="body2">
                  MongoDB 服务暂时不可用
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleSwitchToLegacy}
                    sx={{ ml: 1 }}
                  >
                    切换到旧系统注册
                  </Link>
                </Typography>
              </Box>
            </Alert>
          )}

          {/* 错误提示 */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* 成功提示 */}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* 用户名 */}
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label="用户名"
            name="username"
            autoComplete="username"
            autoFocus
            disabled={loading || !mongoAvailable}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            InputProps={{
              startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            helperText="至少 3 个字符"
          />

          {/* 邮箱 */}
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="邮箱地址"
            name="email"
            autoComplete="email"
            disabled={loading || !mongoAvailable}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            InputProps={{
              startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />

          {/* 密码 */}
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="密码"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="new-password"
            disabled={loading || !mongoAvailable}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              endAdornment: (
                <Button
                  onClick={() => setShowPassword(!showPassword)}
                  sx={{ minWidth: 0 }}
                  tabIndex={-1}
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </Button>
              ),
            }}
            helperText="至少 6 位"
          />

          {/* 确认密码 */}
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="确认密码"
            type={showConfirmPassword ? 'text' : 'password'}
            id="confirmPassword"
            autoComplete="new-password"
            disabled={loading || !mongoAvailable}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            InputProps={{
              startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              endAdornment: (
                <Button
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  sx={{ minWidth: 0 }}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </Button>
              ),
            }}
            error={password !== confirmPassword && confirmPassword.length > 0}
            helperText={
              password !== confirmPassword && confirmPassword.length > 0
                ? '两次密码输入不一致'
                : ' '
            }
          />

          {/* 用户协议 */}
          <FormControlLabel
            control={
              <Checkbox
                value="agreement"
                color="primary"
                checked={agreement}
                onChange={(e) => setAgreement(e.target.checked)}
                disabled={loading || !mongoAvailable}
              />
            }
            label={
              <Typography variant="body2">
                我同意
                <Link href="#" variant="body2">
                  用户协议
                </Link>
                和
                <Link href="#" variant="body2">
                  隐私政策
                </Link>
              </Typography>
            }
          />

          {/* 注册按钮 */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 2, mb: 2 }}
            disabled={loading || !mongoAvailable}
          >
            {loading ? '注册中...' : '注册新账户'}
          </Button>

          <Divider sx={{ my: 2 }}>
            <Chip label="或者" size="small" />
          </Divider>

          <Grid container spacing={2}>
            <Grid item xs>
              <Link component={RouterLink} to="/login-v2" variant="body2">
                已有账户？登录新系统
              </Link>
            </Grid>
          </Grid>

          <Grid container sx={{ mt: 2 }}>
            <Grid item xs>
              <Tooltip title="使用 JSON 文件存储的旧系统">
                <Link
                  component={RouterLink}
                  to="/register"
                  variant="body2"
                  color="text.secondary"
                  onClick={() => switchAuthSystem('json')}
                >
                  <ArrowBackIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  返回旧系统注册
                </Link>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* 新系统特性介绍 */}
      <Paper sx={{ mt: 3, p: 3 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          🎉 新系统特性
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarsIcon color="primary" fontSize="small" />
              <Typography variant="body2">6 级会员体系</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarsIcon color="warning" fontSize="small" />
              <Typography variant="body2">积分成长系统</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarsIcon color="success" fontSize="small" />
              <Typography variant="body2">SHA256 密码加密</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarsIcon color="info" fontSize="small" />
              <Typography variant="body2">完整操作日志</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default MongoRegister;
